/**
 * Laufzeitkonfiguration der Plattform.
 *
 * Zwei Quellen, in dieser Reihenfolge:
 *
 *  1. `window.__BERKY_CONFIG__` — schreibt der Container beim Start aus
 *     Umgebungsvariablen in eine `config.js`. Dadurch läuft dasselbe Image in
 *     Test und Produktion, ohne neu zu bauen.
 *  2. `import.meta.env` — die `.env.local` beim Entwickeln.
 *
 * Ohne beides läuft die Plattform im Mock-Modus mit einem erfundenen Nutzer.
 * Das ist Absicht: ein neuer Modulentwickler soll ohne Konfiguration loslegen.
 */

export type AuthMode = 'mock' | 'entra'
export type SystemEnvironment = 'local' | 'test' | 'production'

export interface MockUserConfig {
  id: string
  name: string
  email: string
  /** `['*']` bedeutet: alle Rollen. Zum Testen von Rollen-Guards überschreiben. */
  roles: string[]
}

export interface RuntimeConfig {
  authMode: AuthMode
  /** Fachliche Umgebung, die im Plattform-Header angezeigt wird. */
  systemEnvironment: SystemEnvironment
  /** Entra Verzeichnis-ID (Tenant). Nur im Modus `entra` nötig. */
  entraTenantId: string
  /** Client-ID der SPA-App-Registrierung. Nur im Modus `entra` nötig. */
  entraClientId: string
  /** Scope, der beim Login angefragt wird (Plattform-API). */
  platformScope: string
  mockUser: MockUserConfig
}

declare global {
  interface Window {
    __BERKY_CONFIG__?: Partial<
      Omit<RuntimeConfig, 'mockUser'> & { mockUser: Partial<MockUserConfig> }
    >
  }
}

/**
 * Zugriff auf die Vite-Umgebungsvariablen ohne Abhängigkeit von `vite/client`.
 *
 * Grund: Dieses Paket wird auch im Typecheck von Modulen mitgeprüft, und dort
 * sind die Vite-Typen nicht eingebunden. Ein Modulentwickler soll dafür nichts
 * konfigurieren müssen.
 */
function env(key: string): string | undefined {
  const meta = import.meta as unknown as { env?: Record<string, unknown> }
  const value = meta.env?.[key]
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined
}

let cached: RuntimeConfig | undefined

/**
 * Liest die Laufzeitkonfiguration einmalig aus und merkt sie sich.
 *
 * @example
 * ```ts
 * if (getRuntimeConfig().authMode === 'mock') { ... }
 * ```
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (cached) return cached

  const injected = typeof window !== 'undefined' ? window.__BERKY_CONFIG__ : undefined
  const mode = injected?.authMode ?? env('VITE_AUTH_MODE') ?? 'mock'

  const resolved: RuntimeConfig = {
    authMode: mode === 'entra' ? 'entra' : 'mock',
    systemEnvironment: resolveSystemEnvironment(
      injected?.systemEnvironment ?? env('VITE_BERKY_SYSTEM_ENVIRONMENT'),
    ),
    entraTenantId: injected?.entraTenantId ?? env('VITE_ENTRA_TENANT_ID') ?? '',
    entraClientId: injected?.entraClientId ?? env('VITE_ENTRA_CLIENT_ID') ?? '',
    platformScope: injected?.platformScope ?? env('VITE_ENTRA_PLATFORM_SCOPE') ?? '',
    mockUser: {
      id: injected?.mockUser?.id ?? env('VITE_MOCK_USER_ID') ?? 'mock-user',
      name: injected?.mockUser?.name ?? env('VITE_MOCK_USER_NAME') ?? 'Test Nutzer',
      email: injected?.mockUser?.email ?? env('VITE_MOCK_USER_EMAIL') ?? 'test@berky.de',
      roles: injected?.mockUser?.roles ??
        env('VITE_MOCK_USER_ROLES')
          ?.split(',')
          .map((role) => role.trim())
          .filter(Boolean) ?? ['*'],
    },
  }

  if (
    resolved.authMode === 'entra' &&
    (!resolved.entraTenantId || !resolved.entraClientId || !resolved.platformScope)
  ) {
    const missing = [
      !resolved.entraTenantId ? 'VITE_ENTRA_TENANT_ID' : null,
      !resolved.entraClientId ? 'VITE_ENTRA_CLIENT_ID' : null,
      !resolved.platformScope ? 'VITE_ENTRA_PLATFORM_SCOPE' : null,
    ].filter((value): value is string => value !== null)

    throw new Error(
      [
        'Die Anmeldung ist auf "entra" gestellt, aber es fehlen Angaben.',
        `Nicht gesetzt: ${missing.join(', ')}`,
        'Benötigt werden Tenant-ID, Client-ID und Plattform-Scope',
        '(bzw. die entsprechenden Werte in der config.js des Containers).',
        'Zum Entwickeln ohne Entra: VITE_AUTH_MODE=mock setzen oder die Variable weglassen.',
      ].join('\n'),
    )
  }

  cached = resolved
  return resolved
}

function resolveSystemEnvironment(value: string | undefined): SystemEnvironment {
  if (value === 'production') return 'production'
  if (value === 'test') return 'test'
  return 'local'
}

/** Nur für Tests: erzwingt beim nächsten Aufruf ein erneutes Einlesen. */
export function resetRuntimeConfig(): void {
  cached = undefined
}
